import { UserSettings } from 'src/types/UserSettings.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { sortAccessories } from 'src/modules/common/utils/sortAccessories.ts';
import { SensorsAccessoriesPageData } from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { LocationState } from 'src/sensors/LocationState.ts';
import { Accessory, prepareAccessories } from 'src/common/prepareAccessories.ts';
import { subtotalAccessories } from 'src/common/subtotalAccessories.ts';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';

export const calculateTotals = (
    {
        sensor,
        accessories: sensorAccessories,
    }: SensorsAccessoriesPageData['accessories'],
    userSettings: UserSettings,
    checkoutState: LocationState,
    currency: string | null,
    pipeConnecotrsEnabled: boolean,
): {
    subtotal: number;
    accessoriesSubtotal: number;
    total: number;
    currentCurrency: string;
    pipeConnectors: Accessory<SensorsAccessoriesPageData['accessories']['accessories']['0']>[];
    pipeConnectorsSubtotal: number;
    accessories: Accessory<SensorsAccessoriesPageData['accessories']['accessories']['0']>[];
    pricePerUnit: number;
} => {
    const sensorPrice = getPriceValue(sensor, userSettings.shoppingCenter as keyof typeof sensor, currency);
    const currentCurrency = sensorPrice?.currency || currency || 'USD';
    const pricePerUnit = sensorPrice ? sensorPrice.value : 0;
    const subtotal = pricePerUnit * checkoutState.qty;

    const { isRecommended } = parseRecommended(sensor.connectors);

    const allAccessories = prepareAccessories<SensorsAccessoriesPageData['accessories']['accessories']['0']>(
        sensorAccessories,
        checkoutState.selectedAccessories,
        userSettings,
        currency,
        (a, b) => sortAccessories(a.accessory, b.accessory),
    );

    const pipeConnectors = allAccessories.filter(({ accessory }) => isRecommended(accessory.item.Label))
        .map((connector) => ({ ...connector, qty: connector.qty * (pipeConnecotrsEnabled ? checkoutState.qty : 1) }));
    const pipeConnectorsSubtotal = subtotalAccessories(pipeConnectors);

    const accessories = allAccessories.filter(({ accessory }) => !isRecommended(accessory.item.Label));

    const accessoriesSubtotal = subtotalAccessories<
    SensorsAccessoriesPageData['accessories']['accessories']['0']['item']
    >(accessories);

    const total = subtotal + pipeConnectorsSubtotal + accessoriesSubtotal;

    return {
        subtotal,
        accessoriesSubtotal,
        total,
        currentCurrency,
        pipeConnectors,
        pipeConnectorsSubtotal,
        accessories,
        pricePerUnit,
    };
};
